import React, {useEffect, useState} from 'react';
import {Stack, TextField, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, FormHelperText, InputAdornment, IconButton} from '@mui/material';
import createAccountModel from './models/createAccountModel'
import {Field, useFormikContext} from "formik";
import { styled } from '@mui/material/styles';
import { Person, Business, Email, Lock, Visibility, VisibilityOff, Wc } from '@mui/icons-material';

const { formField: {createAccountName, createAccountDepartment, createAccountEmail, createAccountPass, createAccountGender} } = createAccountModel;

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    transition: 'all 0.3s ease',
  },
  '& .MuiInputAdornment-root': {
    color: theme.palette.text.secondary,
  }
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  marginTop: '8px',
  '& .MuiFormLabel-root': {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
  },
  '& .MuiRadio-root': {
    color: theme.palette.primary.main,
  },
  '& .MuiFormControlLabel-label': {
    fontSize: '0.9rem',
  }
}));

interface OwnProps {
  apiError?: string
}

const CreateAccountForm = (props: OwnProps) => {
  const {errors, touched, setErrors} = useFormikContext<any>();
  const [showPassword, setShowPassword] = useState(false);
  
  useEffect(() => {
    setErrors({[createAccountPass.name]: props.apiError})
  }, [props.apiError, setErrors])
  
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

    return (
        <Stack width="100%" spacing={3} pt={2}>
            <Field
                as={StyledTextField}
                variant="outlined"
                fullWidth
                name={createAccountName.name}
                label={createAccountName.label}
                required={createAccountName.required}
                inputProps={{required: false}}
                type="text"
                autoComplete="name"
                error={touched[createAccountName.name] && Boolean(errors[createAccountName.name])}
                helperText={(touched[createAccountName.name] && errors[createAccountName.name]) ? errors[createAccountName.name] : ''}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Person fontSize="small" />
                        </InputAdornment>
                    ),
                }}
            />

            <Field
                as={StyledTextField}
                variant="outlined"
                fullWidth
                name={createAccountDepartment.name}
                label={createAccountDepartment.label}
                required={createAccountDepartment.required}
                inputProps={{required: false}}
                type="text"
                autoComplete="organization"
                error={touched[createAccountDepartment.name] && Boolean(errors[createAccountDepartment.name])}
                helperText={(touched[createAccountDepartment.name] && errors[createAccountDepartment.name]) ? errors[createAccountDepartment.name] : ''}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Business fontSize="small" />
                        </InputAdornment>
                    ),
                }}
            />

            <Field
                as={StyledTextField}
                variant="outlined"
                fullWidth
                name={createAccountEmail.name}
                label={createAccountEmail.label}
                required={createAccountEmail.required}
                inputProps={{required: false}}
                type="email"
                autoComplete="email"
                error={touched[createAccountEmail.name] && Boolean(errors[createAccountEmail.name])}
                helperText={(touched[createAccountEmail.name] && errors[createAccountEmail.name]) ? errors[createAccountEmail.name] : ''}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Email fontSize="small" />
                        </InputAdornment>
                    ),
                }}
            />

            <Field
                as={StyledTextField}
                variant="outlined"
                fullWidth
                name={createAccountPass.name}
                label={createAccountPass.label}
                required={createAccountPass.required}
                inputProps={{required: false}}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                error={touched[createAccountPass.name] && Boolean(errors[createAccountPass.name])}
                helperText={(touched[createAccountPass.name] && errors[createAccountPass.name]) ? errors[createAccountPass.name] : ''}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Lock fontSize="small" />
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                edge="end"
                                size="small"
                            >
                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />

            <StyledFormControl error={touched[createAccountGender.name] && Boolean(errors[createAccountGender.name])}>
                <FormLabel component="legend">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Wc fontSize="small" />
                        <span>{createAccountGender.label}</span>
                    </Stack>
                </FormLabel>
                <Field name={createAccountGender.name}>
                    {({ field }: any) => (
                        <RadioGroup {...field} row sx={{ justifyContent: 'space-around', marginTop: '8px' }}>
                            <FormControlLabel value="male" control={<Radio />} label="Male" />
                            <FormControlLabel value="female" control={<Radio />} label="Female" />
                        </RadioGroup>
                    )}
                </Field>
                {touched[createAccountGender.name] && errors[createAccountGender.name] && (
                    <FormHelperText>{String(errors[createAccountGender.name])}</FormHelperText>
                )}
            </StyledFormControl>
        </Stack>
    )
}

export default CreateAccountForm
